/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Neeraj Raj
 */

import {
  BreadcrumbItem,
  Product,
  StaticGroupedFAQ,
  VariantItemPdp,
  seoFAQItem,
} from "../common/lib/types";
import { getLangFromLocale, getValueBasedOnLanguage, hasArrayElements } from "./commonUtils";
import { baseURL, defaultLocale } from "../runtimeConfig/public";
import { getAttributeValue } from "./productUtils";
import { pageTypes, SaleorAttributes } from "./constants";
import { getCountryCodeFromLocale } from "@/utils/common";
import { toHTML } from "@portabletext/to-html";

interface SchemaMarkUpProps {
  pageType: string;
  locale?: string;
  pageData?: Record<string, any>;
  breadCrumbs?: BreadcrumbItem[];
}

interface FaqSchema {
  question: string;
  answer: string;
}

export const getSchemaMarkUpsByPageType = (schemaRequest: SchemaMarkUpProps) => {
  const schemaMarkups: Array<Record<string, any>> = [];
  const { pageType, locale, pageData, breadCrumbs = [] } = schemaRequest || {};
  switch (pageType) {
    case pageTypes.HOME:
      schemaMarkups.push(getWebSiteSchema(locale));
      schemaMarkups.push(getOrganizationSchema(locale));
      schemaMarkups.push(getBreadcrumbSchema(breadCrumbs, locale));
      break;
    case pageTypes.PDP:
      schemaMarkups.push(getProductSchema(locale, pageData?.product, pageData?.selectedVariant));
      schemaMarkups.push(getBreadcrumbSchema(breadCrumbs, locale));
      break;
    case pageTypes.CLP:
    case pageTypes.PLP:
    case pageTypes.BLP:
      if (pageData?.faqSchema) {
        const faqSchemaMarkup = convertFaqSchemaMarkup(pageData.faqSchema);
        schemaMarkups.push(getFaqSchema(faqSchemaMarkup));
      }
      schemaMarkups.push(getBreadcrumbSchema(breadCrumbs, locale));
      break;
    case pageTypes.CONTACT_US:
      schemaMarkups.push(getBreadcrumbSchema(breadCrumbs, locale));
      break;
    case pageTypes.BDP:
      // Blog Detail Page - BlogPosting schema
      if (pageData?.blogData && pageData?.slug) {
        schemaMarkups.push(
          getBlogPostingSchema(locale, pageData.blogData, pageData.slug, pageData?.publisherLogo),
        );
      }
      break;
    case pageTypes.STATIC:
      const { faqSchema, slug } = (pageData || {}) as {
        slug?: string;
        faqSchema: StaticGroupedFAQ | undefined;
      };
      if (slug?.includes("about-doctor-m")) schemaMarkups.push(getOrganizationSchema(locale));
      if (slug?.includes("faq") && faqSchema) {
        const faqSchemaMarkup = convertFaqSchemaMarkupFromStaticFAQPage(faqSchema);
        schemaMarkups.push(getFaqSchema(faqSchemaMarkup));
        if (pageData?.seoFAQs) {
          const faqSchemaMarkup = convertFaqSchemaMarkup(pageData.seoFAQs);
          schemaMarkups.push(getFaqSchema(faqSchemaMarkup));
        }
      }
      if (pageData?.seoFAQs && !slug?.includes("faq")) {
        const faqSchemaMarkup = convertFaqSchemaMarkup(pageData.seoFAQs);
        schemaMarkups.push(getFaqSchema(faqSchemaMarkup));
      }
      break;
    case pageTypes.BLOGS:
    case pageTypes.GUIDES:
      if (pageData?.seoFAQs) {
        const faqSchemaMarkup = convertFaqSchemaMarkup(pageData.seoFAQs);
        schemaMarkups.push(getFaqSchema(faqSchemaMarkup));
      }
      break;
    default:
      schemaMarkups.push(getOrganizationSchema(locale));
      break;
  }
  return schemaMarkups;
};

const getWebSiteSchema = (locale: string = defaultLocale) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: `${baseURL}/${locale}/`,
    potentialAction: {
      "@type": "SearchAction",
      target: `${baseURL}/${locale}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
};

const getOrganizationSchema = (locale: string = defaultLocale) => {
  const lang = getLangFromLocale(locale);
  const country = getCountryCodeFromLocale(locale);
  const isArabic = lang === "ar";
  const contactType = isArabic ? "خدمة العملاء" : "customer service";

  const countryData: Record<string, { address: any; telephone: string; areaServed: string }> = {
    ae: {
      address: {
        streetAddress: isArabic
          ? "مكتب رقم 218، الطابق الثاني، قطعة 367 - 1003، المبنى بجوار فندق متروبوليتان، منطقة أم الشيف، شارع الشيخ زايد"
          : "Office# 218, 2nd Floor, Plot 367 - 1003, The Building next to Metropolitan Hotel, Umm Al Sheif area, Sheikh Zayed Road",
        addressLocality: isArabic ? "دبي" : "Dubai",
        postalCode: "31170",
        addressCountry: "AE",
      },
      telephone: "+97144986441",
      areaServed: "AE",
    },
    sa: {
      address: {
        streetAddress: isArabic
          ? "مركز بن سليمان، الطابق الأول، طريق الأمير سلطان، حي الخالدية"
          : "Bin Sulaiman Center, 1st Floor, Prince Sultan Road, Al Khalidiyah",
        addressLocality: isArabic ? "جدة" : "Jeddah",
        postalCode: "7540",
        addressCountry: "SA",
      },
      telephone: "8008851021",
      areaServed: "SA",
    },
  };

  const data = countryData[country];

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: isArabic ? "دكتور ام" : "Doctor M",
    url: `${baseURL}/${locale}/`,
    logo: "https://cdn.sanity.io/images/rrych78l/drm-uat/155c8046f8501e6e3074272008343986d161656c-787x258.png",
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: data?.telephone,
        contactType,
        areaServed: data?.areaServed,
      },
    ],
    address: {
      "@type": "PostalAddress",
      ...data?.address,
    },
    sameAs: [
      "https://www.facebook.com/DoctorM.Optical",
      "https://www.instagram.com/doctorm.eyewear",
      "https://x.com/doctormoptics",
      "https://www.tiktok.com/@doctorm.eyewear",
      "https://www.youtube.com/@Doctorm.eyewear",
      "https://www.snapchat.com/@doctorm.eyewear",
    ],
  };
};

const getProductSchema = (
  locale: string = defaultLocale,
  product: Product,
  selectedVariant: VariantItemPdp,
) => {
  const language = getLangFromLocale(locale);
  const currency = selectedVariant?.pricing?.price?.currency;
  const productImage = hasArrayElements(product?.selectedVariantMedia)
    ? product?.selectedVariantMedia?.[0]?.url
    : "";
  const description = getAttributeValue(
    selectedVariant?.attributes,
    SaleorAttributes.DESCRIPTION,
    null,
    language,
  );
  const salePrice =
    (selectedVariant?.pricing?.price?.gross?.amount || 0) > 0
      ? selectedVariant?.pricing?.price?.gross?.amount
      : 0.0;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product?.selectedVariantName ?? "",
    image: productImage ?? "",
    description: description ?? "",
    brand: {
      "@type": "Brand",
      name:
        getValueBasedOnLanguage(
          language,
          product?.brand,
          "name",
          "name",
          product?.brand?.translation,
        ) ?? "",
    },
    // sku: selectedVariant?.sku ?? "",
    offers: {
      "@type": "Offer",
      url: `${baseURL}/${locale}${selectedVariant?.landingPageBaseUrl || `/${product?.slug}/${selectedVariant?.id}`}`,
      priceCurrency: currency,
      price: salePrice ?? 0.0,
      availability: `https://schema.org/${selectedVariant?.quantityAvailable > 0 ? "InStock" : "OutOfStock"}`,
      itemCondition: "https://schema.org/NewCondition",
    },
  };
};

const constructBreadcrumbItem = (breadCrumbs: BreadcrumbItem[], locale: string) => {
  const lang = getLangFromLocale(locale);
  let listElements: Array<Record<string, any>> = [];
  listElements.push({
    "@type": "ListItem",
    position: 1,
    name: lang === "ar" ? "الصفحة الرئيسية" : "Home",
    item: `${baseURL}/${locale}/`,
  });
  if (hasArrayElements(breadCrumbs)) {
    breadCrumbs.forEach((item, index) => {
      listElements.push({
        "@type": "ListItem",
        position: index + 2,
        name: item?.label || "",
        item: `${baseURL}/${locale}${item?.href || ""}`,
      });
    });
  }
  return listElements;
};

const getBreadcrumbSchema = (breadCrumbs: BreadcrumbItem[], locale: string = defaultLocale) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: constructBreadcrumbItem(breadCrumbs, locale),
});

const constructItemList = (itemList: string[]) => {
  let listElements: Array<Record<string, any>> = [];
  if (hasArrayElements(itemList)) {
    listElements = itemList.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item,
    }));
  }
  return listElements;
};

const getItemListSchema = (productNames: string[], title: string) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: constructItemList(productNames),
  itemListOrder: "https://schema.org/ItemListOrderDescending",
  name: title,
});

const getFaqSchema = (faqQuestions: FaqSchema[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqQuestions.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
};

const convertFaqSchemaMarkupFromStaticFAQPage = (faqSchema: StaticGroupedFAQ): FaqSchema[] => {
  return faqSchema.items.flatMap(({ items }) => {
    return items.map(({ title, content }) => ({
      question: title,
      answer: toHTML(content),
    }));
  });
};

const convertFaqSchemaMarkup = (faqSchema: seoFAQItem[]): FaqSchema[] => {
  return faqSchema.map(({ title, content }) => ({
    question: title,
    answer: toHTML(content),
  }));
};

/**
 * Generate BlogPosting schema for blog article pages
 *
 * @param locale - Current locale (e.g., 'sa-en', 'sa-ar')
 * @param blogData - Blog data from Sanity CMS
 * @param slug - Blog slug from URL
 * @param publisherLogo - Publisher logo URL
 * @returns BlogPosting schema object
 */
const getBlogPostingSchema = (
  locale: string = defaultLocale,
  blogData: any,
  slug: string,
  publisherLogo?: string,
) => {
  const lang = getLangFromLocale(locale);
  const inLanguage = lang === "ar" ? "ar" : "en";

  // Construct the blog URL
  const blogUrl = `${baseURL}/${locale}/blogs/${slug}`;

  // Get the large screen image URL (server-side, so we use large screen only)
  const imageUrl = blogData?.heroSection?.media?.largeScreenMediaUrl || "";

  // Format the published date to ISO 8601 format
  const datePublished = blogData?.publishedDate
    ? new Date(blogData.publishedDate).toISOString()
    : new Date().toISOString();

  // Use provided logo
  const logoUrl = publisherLogo;

  // Build the BlogPosting schema
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blogData?.title || "",
    description: blogData?.subtitle || "",
    image: imageUrl,
    author: {
      "@type": "Organization",
      name: "Doctor M",
    },
    publisher: {
      "@type": "Organization",
      name: "Doctor M",
      logo: {
        "@type": "ImageObject",
        url: logoUrl,
      },
    },
    datePublished: datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": blogUrl,
    },
    inLanguage: inLanguage,
  };

  // Add optional fields if available
  if (blogData?.readingTime) {
    schema.timeRequired = `PT${blogData.readingTime}M`; // ISO 8601 duration format
  }

  // Add keywords from tags if available
  if (blogData?.tags && hasArrayElements(blogData.tags)) {
    schema.keywords = blogData.tags.map((tag: any) => tag.title).join(", ");
  }

  // Add article section/category if available
  if (blogData?.blogType) {
    schema.articleSection = blogData.blogType;
  }

  return schema;
};

export const dummy = null;
